import { AxiosRequestConfig } from 'axios';
import api from './client';

// Helm API - Role-based personal dashboard
export const helmAPI = {
  // Get full dashboard data (role-aware)
  getData: (organizationId: string | number) =>
    api.get(`/organizations/${organizationId}/helm`),

  // Get just the user's role (lightweight)
  getRole: (organizationId: string | number) =>
    api.get(`/organizations/${organizationId}/helm/role`),

  // Help Requests
  createHelpRequest: (
    organizationId: string | number,
    data: {
      taskId?: string;
      requestType: 'extension' | 'clarification' | 'help' | 'other' | 'need_discussion' | 'consent_withdrawal';
      message: string;
    }
  ) => api.post(`/organizations/${organizationId}/help-requests`, data),

  getHelpRequests: (organizationId: string | number, status?: string) =>
    api.get(`/organizations/${organizationId}/help-requests`, {
      params: status ? { status } : undefined,
    }),

  respondToHelpRequest: (
    organizationId: string | number,
    requestId: string,
    data: {
      status: 'approved' | 'denied' | 'resolved';
      responseMessage?: string;
    }
  ) =>
    api.patch(
      `/organizations/${organizationId}/help-requests/${requestId}/respond`,
      data
    ),

  // Phase 2: Specific help request actions
  approveExtension: (
    organizationId: string | number,
    requestId: string,
    data: {
      newDueDate: string;
      newDueTime?: string | null;
      responseMessage?: string;
      sendNotification?: boolean;
    }
  ) =>
    api.patch(
      `/organizations/${organizationId}/help-requests/${requestId}/approve-extension`,
      data
    ),

  denyRequest: (
    organizationId: string | number,
    requestId: string,
    data: {
      reason: string;
      sendNotification?: boolean;
    }
  ) =>
    api.patch(
      `/organizations/${organizationId}/help-requests/${requestId}/deny`,
      data
    ),

  replyToRequest: (
    organizationId: string | number,
    requestId: string,
    data: {
      message: string;
      sendNotification?: boolean;
    }
  ) =>
    api.patch(
      `/organizations/${organizationId}/help-requests/${requestId}/reply`,
      data
    ),

  // Phase 3: Bulk help request actions
  bulkApproveExtensions: (
    organizationId: string | number,
    data: {
      requestIds: string[];
      newDueDate: string;
      newDueTime?: string | null;
      responseMessage?: string;
      sendNotification?: boolean;
    }
  ) =>
    api.post(
      `/organizations/${organizationId}/help-requests/bulk-approve-extensions`,
      data
    ),

  bulkDenyRequests: (
    organizationId: string | number,
    data: {
      requestIds: string[];
      reason: string;
      sendNotification?: boolean;
    }
  ) =>
    api.post(
      `/organizations/${organizationId}/help-requests/bulk-deny`,
      data
    ),

  // Spec 031: Read Status Tracking
  getUnreadCount: (organizationId: string | number, config?: AxiosRequestConfig) =>
    api.get<{
      tasks: number;
      approvals: number;
      helpRequests: number;
      activity: number;
      total: number;
    }>(`/organizations/${organizationId}/helm/unread-count`, config),

  markRead: (
    organizationId: string | number,
    activityType: string,
    referenceId: number
  ) =>
    api.post(`/organizations/${organizationId}/helm/mark-read`, {
      activityType,
      referenceId,
    }),

  markSectionRead: (
    organizationId: string | number,
    section: 'tasks' | 'approvals' | 'helpRequests' | 'activity'
  ) =>
    api.post<{ success: boolean; markedCount: number }>(
      `/organizations/${organizationId}/helm/mark-section-read`,
      { section }
    ),

  markAllRead: (organizationId: string | number) =>
    api.post<{ success: boolean; markedCount: number }>(
      `/organizations/${organizationId}/helm/mark-all-read`
    ),

  /** SPEC-048: Get DM & Community widget data */
  getWidgets: (organizationId: string | number) =>
    api.get<{
      messages: {
        conversations: Array<{
          id: string;
          participantName: string;
          participantAvatar?: string;
          lastMessage: string;
          lastMessageAt: string;
          unreadCount: number;
          isOnline?: boolean;
        }>;
        totalUnread: number;
      };
      communities: {
        communities: Array<{
          id: string;
          name: string;
          avatar?: string;
          memberCount: number;
          unreadCount: number;
          lastActivity?: {
            type: 'post' | 'comment' | 'event';
            preview: string;
            timestamp: string;
          };
          role: 'member' | 'moderator' | 'admin' | 'owner';
        }>;
        recentActivity: Array<{
          id: string;
          communityId: string;
          communityName: string;
          type: 'post' | 'comment' | 'event' | 'announcement';
          preview: string;
          timestamp: string;
          authorName: string;
        }>;
        suggestions: Array<{
          id: string;
          name: string;
          avatar?: string;
          memberCount: number;
          description: string;
        }>;
        totalUnread: number;
      };
      discovery: {
        matchCount: number;
        unreadMessages: number;
        recentMatches: Array<{
          matchId: string;
          userId: number;
          displayName: string | null;
          thumbnailUrl: string | null;
          matchedAt: string;
        }>;
      };
    }>(`/organizations/${organizationId}/helm/widgets`),

  // =============================================================================
  // Spec 109: Behavioral Pattern Analysis (Dom Insights)
  // =============================================================================

  /** Get behavioral insights for a user in the hierarchy */
  getInsights: (
    organizationId: string | number,
    options?: {
      userId?: string;
      period?: number;
      types?: string[];
    }
  ) => {
    const params = new URLSearchParams();
    if (options?.userId) params.append('userId', options.userId);
    if (options?.period) params.append('period', options.period.toString());
    if (options?.types) params.append('types', options.types.join(','));
    const queryString = params.toString();
    return api.get<{
      success: boolean;
      data: Array<{
        type: string;
        title: string;
        description: string;
        confidence: 'low' | 'medium' | 'high';
        dataPoints: number;
        period: string;
        actionSuggestion: string | null;
        value?: number;
        trend?: 'up' | 'down' | 'stable';
      }>;
    }>(`/organizations/${organizationId}/helm/insights${queryString ? `?${queryString}` : ''}`);
  },

  getAttention: (organizationId: string | number) =>
    api.get<{ data: { snoozed?: boolean; snoozeUntil?: string; category?: string } | import('../../hooks/useAttention').StripModel }>(
      `/organizations/${organizationId}/helm/attention`
    ),

  getAttentionSnoozeStatus: (organizationId: string | number) =>
    api.get<{ data: { snoozed: boolean; snoozeUntil?: string } }>(
      `/organizations/${organizationId}/helm/attention/snooze-status`
    ),

  snoozeAttention: (organizationId: string | number, durationMs: number, category: string) =>
    api.post<{ data: { ok: boolean } }>(
      `/organizations/${organizationId}/helm/attention/snooze`,
      { durationMs, category }
    ),

  clearAttentionSnooze: (organizationId: string | number) =>
    api.delete<{ data: { ok: boolean } }>(
      `/organizations/${organizationId}/helm/attention/snooze`
    ),

  // Spec 203: Cancel a pending help request (submissive, 15-min window)
  cancelHelpRequest: (organizationId: string | number, requestId: string) =>
    api.delete<{ data: { success: boolean } }>(
      `/organizations/${organizationId}/help-requests/${requestId}/cancel`
    ),

  // Spec 203: Withdraw from a task (dominant action)
  withdrawFromTask: (organizationId: string | number, taskId: string) =>
    api.post<{ data: { success: boolean; taskId: string } }>(
      `/organizations/${organizationId}/tasks/${taskId}/withdraw`
    ),
};
